import React from 'react';
import { useNode } from '@craftjs/core';
import { useTheme } from '../../../contexts/ThemeContext';

const fontFamilies = [
  { value: 'Play, sans-serif', label: 'Play' },
  { value: 'Unbounded, sans-serif', label: 'Unbounded' },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Arial, sans-serif', label: 'Arial' },
];

const backgroundColors = [
  { value: 'transparent', label: 'None' },
  { value: '#A2FF00', label: 'Accent' },
  { value: '#f3f4f6', label: 'Light Gray' },
  { value: '#1f2937', label: 'Dark Gray' },
  { value: '#fef3c7', label: 'Yellow' },
  { value: '#dbeafe', label: 'Blue' },
];

export const HeadingBlock = ({ text, level, textAlign, fontFamily, backgroundColor, textColor }) => {
  const { isDark } = useTheme();
  const { connectors: { connect, drag }, selected, actions: { setProp } } = useNode((state) => ({
    selected: state.events.selected
  }));

  const sizes = {
    h1: 'text-4xl font-bold',
    h2: 'text-3xl font-bold',
    h3: 'text-2xl font-semibold',
  };

  const Tag = level || 'h2';

  // Get responsive background color
  const getBgStyle = () => {
    if (!backgroundColor || backgroundColor === 'transparent') return {};
    if (isDark && backgroundColor === '#f3f4f6') return { backgroundColor: '#27272a' };
    if (isDark && backgroundColor === '#fef3c7') return { backgroundColor: 'rgba(180, 83, 9, 0.3)' };
    if (isDark && backgroundColor === '#dbeafe') return { backgroundColor: 'rgba(30, 58, 138, 0.3)' };
    return { backgroundColor };
  };

  return (
    <div
      ref={(ref) => ref && connect(drag(ref))}
      className={`p-4 rounded-lg transition-all ${
        selected ? 'ring-2 ring-[#A2FF00]' : ''
      } ${isDark ? 'hover:bg-zinc-800/50' : 'hover:bg-gray-100'}`}
      style={getBgStyle()}
    >
      <Tag
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => setProp(props => props.text = e.target.innerText)}
        className={`outline-none ${sizes[level]}`}
        style={{ 
          textAlign: textAlign || 'left',
          fontFamily: fontFamily || 'Unbounded, sans-serif',
          color: textColor || (isDark ? '#ffffff' : '#111827')
        }}
      >
        {text}
      </Tag>
    </div>
  );
};

HeadingBlock.craft = {
  displayName: 'Heading',
  props: {
    text: 'Heading',
    level: 'h2',
    textAlign: 'left',
    fontFamily: 'Unbounded, sans-serif',
    backgroundColor: 'transparent',
    textColor: ''
  },
  related: {
    toolbar: HeadingBlockSettings
  }
};

function HeadingBlockSettings() {
  const { actions: { setProp }, props } = useNode((node) => ({
    props: node.data.props
  }));
  const { isDark } = useTheme();

  return (
    <div className="space-y-4">
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Font Family
        </label>
        <select
          value={props.fontFamily || 'Unbounded, sans-serif'}
          onChange={(e) => setProp(props => props.fontFamily = e.target.value)}
          className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
        >
          {fontFamilies.map((font) => (
            <option key={font.value} value={font.value}>{font.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Heading Level
        </label>
        <div className="flex gap-2">
          {['h1', 'h2', 'h3'].map((h) => (
            <button
              key={h}
              onClick={() => setProp(props => props.level = h)}
              className={`flex-1 px-3 py-2 rounded-lg border uppercase ${
                props.level === h
                  ? 'bg-[#A2FF00] text-black border-[#A2FF00]'
                  : isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'
              }`}
            >
              {h}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Alignment
        </label>
        <div className="flex gap-2">
          {['left', 'center', 'right'].map((align) => (
            <button
              key={align}
              onClick={() => setProp(props => props.textAlign = align)}
              className={`flex-1 px-3 py-2 rounded-lg border capitalize ${
                props.textAlign === align
                  ? 'bg-[#A2FF00] text-black border-[#A2FF00]'
                  : isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'
              }`}
            >
              {align}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Background Color
        </label>
        <div className="grid grid-cols-3 gap-2">
          {backgroundColors.map((color) => (
            <button
              key={color.value}
              onClick={() => setProp(props => props.backgroundColor = color.value)}
              className={`p-2 rounded-lg border flex items-center justify-center ${
                props.backgroundColor === color.value
                  ? 'ring-2 ring-[#A2FF00]'
                  : ''
              } ${isDark ? 'border-zinc-700' : 'border-gray-300'}`}
              title={color.label}
            >
              <div 
                className={`w-6 h-6 rounded ${color.value === 'transparent' ? 'border border-dashed' : ''}`}
                style={{ backgroundColor: color.value === 'transparent' ? 'transparent' : color.value }}
              />
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Text Color
        </label>
        <div className="flex gap-2">
          <input
            type="color"
            value={props.textColor || (isDark ? '#ffffff' : '#111827')}
            onChange={(e) => setProp(props => props.textColor = e.target.value)}
            className="w-10 h-10 rounded cursor-pointer"
          />
          <button
            onClick={() => setProp(props => props.textColor = '')}
            className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
          >
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  );
}

export { HeadingBlockSettings };